import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import FacilityList from "../components/FacilityList";
import RiverList from "../components/RiverList";
import SearchResults from "../components/SearchResults";

const Searchpage = ({ regionsData }) => {
  const getUrlParams = () => {
    const path = window.location.pathname;

    if (path === "/list" || path === "/list/") {
      window.history.replaceState({}, "", "/list/all");
      return { type: "region", value: "all" };
    }

    const searchMatch = path.match(/\/list\/search\/(.+)/);
    if (searchMatch) {
      return { type: "search", value: decodeURIComponent(searchMatch[1]) };
    }

    const riverMatch = path.match(/\/list\/([^/]+)\/([^/]+)\/?$/);
    if (riverMatch) {
      return {
        type: "river",
        region: decodeURIComponent(riverMatch[1]),
        river: decodeURIComponent(riverMatch[2]),
      };
    }

    const regionMatch = path.match(/\/list\/([^/]+)\/?$/);
    if (regionMatch && regionMatch[1] !== "search") {
      return { type: "region", value: decodeURIComponent(regionMatch[1]) };
    }

    return { type: null, value: "" };
  };

  const urlParams = getUrlParams();

  const [selectedRegion, setSelectedRegion] = useState(
    urlParams.type === "region"
      ? urlParams.value
      : urlParams.type === "river"
        ? urlParams.region
        : "all"
  );
  const [selectedRiver, setSelectedRiver] = useState(null);
  const [searchTerm, setSearchTerm] = useState(
    urlParams.type === "search" ? urlParams.value : ""
  );
  const [riverSlugFromUrl, setRiverSlugFromUrl] = useState(
    urlParams.type === "river" ? urlParams.river : null
  );

  const allRivers = useMemo(() => {
    if (!regionsData) return [];
    return regionsData.flatMap(region =>
      (region.rivers || []).map(river => ({
        ...river,
        region_name: region.region_name,
        region_slug: region.region_slug,
        region_id: region.region_id,
      }))
    );
  }, [regionsData]);

  // ✅ Build flat facility list same as Homepage's allFacilities
  const allFacilitiesRaw = useMemo(() => {
    if (!regionsData) return [];
    return regionsData.flatMap(region =>
      (region.rivers || []).flatMap(river => {
        if (river.facilities && river.facilities.length > 0) {
          return river.facilities.map(facility => ({
            facility_id: facility.id || facility.facility_id,
            facility_name: facility.facility_name,
            river_name: river.river_name,
            region_name: region.region_name,
            near_town: facility.near_town || "",
            slug: facility.facility_slug,
            post_id: facility.post_id,
            label: [
              facility.facility_name,
              river.river_name,
              facility.near_town
            ].filter(Boolean).join(", "),
            type: "facility",
            data: {
              ...facility,
              river_name: river.river_name,
              river_id: river.river_id,
              river_slug: river.river_slug,
              region_name: region.region_name,
              region_slug: region.region_slug,
            },
          }));
        }
        return [];
      })
    );
  }, [regionsData]);

  const allFacilities = useMemo(() => {
    return [
      ...new Map(allFacilitiesRaw.map(item => [item.label, item])).values()
    ];
  }, [allFacilitiesRaw]);

  useEffect(() => {
    const params = getUrlParams();
    if (params.type === "search") {
      setSearchTerm(params.value);
      setSelectedRegion("all");
      setSelectedRiver(null);
    } else if (params.type === "region") {
      setSelectedRegion(params.value);
      setSearchTerm("");
      setSelectedRiver(null);
    } else if (params.type === "river") {
      setSelectedRegion(params.region);
      setSearchTerm("");
      setRiverSlugFromUrl(params.river);
    }
  }, []);

  useEffect(() => {
    if (riverSlugFromUrl && allRivers.length) {
      const river = allRivers.find(r => r.river_slug === riverSlugFromUrl);
      if (river) {
        setSelectedRiver(river.river_id);
        setRiverSlugFromUrl(null);
      }
    }
  }, [riverSlugFromUrl, allRivers]);

  const filteredRivers = useMemo(() => {
    return selectedRegion === "all"
      ? allRivers
      : allRivers.filter(r => r.region_slug === selectedRegion);
  }, [selectedRegion, allRivers]);

  const displayedFacilities = useMemo(() => {
    if (!selectedRiver) return [];
    const river = filteredRivers.find(r => r.river_id === selectedRiver);
    return river?.facilities
      ? river.facilities.map(f => ({
        ...f,
        river_name: river.river_name,
        region_name: river.region_name,
      }))
      : [];
  }, [selectedRiver, filteredRivers]);

  // Match only the displayed label text so results stay exact.
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return null;

    const searchLower = searchTerm.trim().toLowerCase();
    const matchedFacilities = allFacilities.filter(item =>
      item.label?.toLowerCase().includes(searchLower)
    );

    // Convert matched facilities into SearchResults-compatible format
    return matchedFacilities.map(item => ({
      type: "facility",
      id: item.facility_id,
      name: item.facility_name,
      region: item.region_name,
      river: item.river_name,
      label: item.label,
      data: item.data,
    }));
  }, [searchTerm, allFacilities]);

  const handleRegionClick = regionSlug => {
    setSelectedRegion(regionSlug);
    setSelectedRiver(null);
    setSearchTerm("");
    window.history.pushState({}, "", regionSlug === "all" ? "/list/all/" : `/list/${regionSlug}`);
  };

  const handleRiverClick = riverId => {
    setSelectedRiver(riverId);
    const river = filteredRivers.find(r => r.river_id === riverId);
    if (river) {
      window.history.pushState(
        {},
        "",
        `/list/${selectedRegion === "all" ? "all" : selectedRegion}/${river.river_slug}`
      );
    }
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedRiver(null);
    setSelectedRegion("all");

    window.history.pushState(
      {},
      "",
      value.trim()
        ? `/list/search/${encodeURIComponent(value)}`
        : "/list/all"
    );
  };

  const selectedRiverData = useMemo(() => {
    return filteredRivers.find(r => r.river_id === selectedRiver);
  }, [selectedRiver, filteredRivers]);

  const headerSearchEl = document.getElementById("header-search-root");

  return (
    <>
      {headerSearchEl && createPortal(
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search rivers and facilities..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button className="searchbar-button"></button>
        </div>,
        headerSearchEl
      )}
      <div className="search-main-left">
        <div className='information-tip'>
          <div className='info-icon'>
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M12 17C12.2833 17 12.521 16.904 12.713 16.712C12.905 16.52 13.0007 16.2827 13 16V12C13 11.7167 12.904 11.4793 12.712 11.288C12.52 11.0967 12.2827 11.0007 12 11C11.7173 10.9993 11.48 11.0953 11.288 11.288C11.096 11.4807 11 11.718 11 12V16C11 16.2833 11.096 16.521 11.288 16.713C11.48 16.905 11.7173 17.0007 12 17ZM12 9C12.2833 9 12.521 8.904 12.713 8.712C12.905 8.52 13.0007 8.28267 13 8C12.9993 7.71733 12.9033 7.48 12.712 7.288C12.5207 7.096 12.2833 7 12 7C11.7167 7 11.4793 7.096 11.288 7.288C11.0967 7.48 11.0007 7.71733 11 8C10.9993 8.28267 11.0953 8.52033 11.288 8.713C11.4807 8.90567 11.718 9.00133 12 9ZM12 22C10.6167 22 9.31667 21.7373 8.1 21.212C6.88334 20.6867 5.825 19.9743 4.925 19.075C4.025 18.1757 3.31267 17.1173 2.788 15.9C2.26333 14.6827 2.00067 13.3827 2 12C1.99933 10.6173 2.262 9.31733 2.788 8.1C3.314 6.88267 4.02633 5.82433 4.925 4.925C5.82367 4.02567 6.882 3.31333 8.1 2.788C9.318 2.26267 10.618 2 12 2C13.382 2 14.682 2.26267 15.9 2.788C17.118 3.31333 18.1763 4.02567 19.075 4.925C19.9737 5.82433 20.6863 6.88267 21.213 8.1C21.7397 9.31733 22.002 10.6173 22 12C21.998 13.3827 21.7353 14.6827 21.212 15.9C20.6887 17.1173 19.9763 18.1757 19.075 19.075C18.1737 19.9743 17.1153 20.687 15.9 21.213C14.6847 21.739 13.3847 22.0013 12 22Z'
                fill='#3E7E23'
              />
            </svg>
          </div>
          <div className='info-text'>
            <p>
              To ensure you have a safe and enjoyable experience on the water,
              view water levels, flow rates and safety information related to
              our hydropower facilities.
            </p>
          </div>
        </div>
        <div className="region-list">
          <ul className="list-unstyled">
            <li>
              <a
                href="javascript:void(0)"
                className={selectedRegion === "all" ? "active" : ""}
                onClick={() => handleRegionClick("all")}
              >
                All Regions
              </a>
            </li>
            {regionsData?.map(region => (
              <li key={region.region_id}>
                <a
                  href="javascript:void(0)"
                  className={selectedRegion === region.region_slug ? "active" : ""}
                  onClick={() => handleRegionClick(region.region_slug)}
                >
                  {region.region_name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="search-main-right">
        {searchResults ? (
          <SearchResults
            results={searchResults}
            searchTerm={searchTerm}
            onRiverClick={handleRiverClick}
            facilityData={regionsData}
          />
        ) : !selectedRiver ? (
          <>
            <div className='title'>
              <h1
                className={
                  selectedRegion !== "all"
                    ? "river-systems-title"
                    : ""
                }
              >
                {selectedRegion !== "all" && (
                  <span
                    className="river-back-button"
                    onClick={() => handleRegionClick("all")}
                  >
                    {/* back icon */}
                  </span>
                )}

                River Systems{" "}
                {selectedRegion === "all"
                  ? "(All)"
                  : `(${regionsData?.find(
                    r => r.region_slug === selectedRegion
                  )?.region_name})`}
              </h1>
            </div>
            <RiverList rivers={filteredRivers} onRiverClick={handleRiverClick} />
          </>
        ) : (
          <>
            <button
              className="river-back-button"
              onClick={() => {
                setSelectedRiver(null);
                window.history.pushState({}, "", selectedRegion === "all" ? "/list/all" : `/list/${selectedRegion}`);
              }}
            >
              <h1>
                {selectedRiverData
                  && `${selectedRiverData.river_name}`
                }
              </h1>
            </button>
            {displayedFacilities.length ? (
              <FacilityList facilityData={displayedFacilities} />
            ) : (
              <h4>No facilities found in this river.</h4>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Searchpage;
